import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOpenAnalysis } from "@/lib/useOpenAnalysis";
import { Ticker } from "@/components/TickerDrawer";
import { useConfirm } from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import Reconsiderations from "@/components/Reconsiderations";
import { RegimePanel } from "@/components/RegimePanel";
import CoachingPanel from "@/components/CoachingPanel";
import CompareScansView from "@/components/CompareScansView";
import { useLiveQuotes, sinceScanPct } from "@/hooks/useLiveQuotes";
import {
  Loader2, TrendingUp, TrendingDown, Eye, Sparkles, Zap, Ban, Filter, Save,
  CheckCircle2, XCircle, Clock, DollarSign, Bookmark, Plus, Star,
} from "lucide-react";

const VERDICT_STYLE = {
  STRONG_BUY:       { tone: "bg-signal-green/20 text-signal-green border-signal-green/50", label: "STRONG BUY", icon: TrendingUp },
  BUY:              { tone: "bg-signal-green/15 text-signal-green border-signal-green/40", label: "BUY", icon: TrendingUp },
  BUY_ON_PULLBACK:  { tone: "bg-signal-blue/15 text-[#0A84FF] border-[#0A84FF]/40", label: "BUY ON PULLBACK", icon: Clock },
  WATCH:            { tone: "bg-muted/20 text-muted-foreground border-border/60", label: "WATCH", icon: Eye },
  HOLD:             { tone: "bg-muted/20 text-muted-foreground border-border/60", label: "HOLD", icon: Eye },
  REDUCE:           { tone: "bg-signal-yellow/15 text-signal-yellow border-signal-yellow/40", label: "REDUCE", icon: TrendingDown },
  TAKE_PARTIAL_PROFIT: { tone: "bg-signal-yellow/15 text-signal-yellow border-signal-yellow/40", label: "TAKE PARTIAL", icon: DollarSign },
  SELL:             { tone: "bg-signal-red/15 text-signal-red border-signal-red/40", label: "SELL", icon: TrendingDown },
  STRONG_SELL:      { tone: "bg-signal-red/20 text-signal-red border-signal-red/60", label: "STRONG SELL", icon: TrendingDown },
  AVOID:            { tone: "bg-signal-red/10 text-signal-red border-signal-red/30", label: "AVOID", icon: Ban },
};

function StateChips({ s }) {
  const chips = [];
  if (s?.already_owned) chips.push({ label: "Owned", tone: "text-signal-green border-current" });
  if (s?.already_proposed) chips.push({ label: "Proposed", tone: "text-[#0A84FF] border-current" });
  if (s?.on_watchlist) chips.push({ label: "Watchlist", tone: "text-muted-foreground border-border/60" });
  if (s?.already_skipped) chips.push({ label: "Skipped", tone: "text-signal-yellow border-current" });
  return chips.length ? (
    <div className="flex gap-1 flex-wrap">{chips.map((c) => (
      <span key={c.label} className={`chip !text-[9px] ${c.tone}`}>{c.label}</span>
    ))}</div>
  ) : null;
}

function ScoreBar({ v, m, label }) {
  const pct = Math.min(100, (v / m) * 100);
  return (
    <div>
      <div className="flex justify-between text-[9px] uppercase mono tracking-widest text-muted-foreground">
        <span>{label}</span><span>{v}/{m}</span>
      </div>
      <div className="h-1 bg-secondary/50 mt-0.5"><div className="h-full bg-[#0A84FF]" style={{ width: `${pct}%` }}/></div>
    </div>
  );
}

function OpportunityCard({ o, onOpen, onDismiss, onWatchlist, liveQuote, priceLabel }) {
  const v = VERDICT_STYLE[o.verdict] || VERDICT_STYLE.WATCH;
  const Icon = v.icon;
  const plan = (o.plans || [])[0] || {};
  const scanPrice = o.price ?? o.current_price;
  const live = liveQuote && liveQuote.status === "OK" ? liveQuote : null;
  const deltaPct = live ? sinceScanPct(scanPrice, live.price) : null;
  const label = live?.price_label || priceLabel || "LIVE";
  return (
    <div className="grid-cell !p-0 flex flex-col hover:border-border transition-colors" data-testid={`opp-card-${o.symbol}`}>
      <div className={`border-b ${v.tone} px-3 py-2 flex items-center gap-2`}>
        <Icon size={13}/>
        <span className="mono uppercase text-[11px] tracking-widest font-medium">{v.label}</span>
        {o.prior_versions_count > 0 && (
          <span className="chip !text-[9px] text-muted-foreground border-border/60" data-testid={`opp-history-${o.symbol}`} title={`${o.prior_versions_count} superseded ${o.prior_versions_count === 1 ? "version" : "versions"}`}>
            +{o.prior_versions_count} prior
          </span>
        )}
        <span className="ml-auto mono text-[10px] uppercase tracking-widest opacity-70">{o.category?.replace("_"," ")}</span>
      </div>
      <div className="p-3 space-y-2 flex-1">
        <div className="flex items-baseline gap-2">
          <div className="mono text-lg font-medium"><Ticker symbol={o.symbol} size="lg"/></div>
          <div className="mono text-xs text-muted-foreground truncate flex-1"
               data-testid="opportunity-card-company-name">
            {(() => {
              // Defensive: hide redundant "TICKER TICKER" when the
              // company-name lookup returned the ticker itself
              // (see market.py yf_lookup degraded-cache fix).
              const name = o.name || o.company || "";
              return name && name.toUpperCase() !== (o.symbol || "").toUpperCase()
                ? name : "";
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="mono text-sm" data-testid={`opp-card-${o.symbol}-scan-price`}>
            <span className="label-cap text-muted-foreground mr-1">SCAN</span>
            ${((scanPrice) || 0).toFixed(2)}
          </div>
          {live && (
            <>
              <div className="mono text-sm" data-testid={`opp-card-${o.symbol}-live-price`}>
                <span className="label-cap text-[#0A84FF] mr-1">{label}</span>
                ${Number(live.price).toFixed(2)}
              </div>
              {deltaPct !== null && (
                <div className={`mono text-[11px] chip !text-[9px] ${
                  deltaPct >= 0
                    ? "text-signal-green border-signal-green/40"
                    : "text-signal-red border-signal-red/40"
                }`} data-testid={`opp-card-${o.symbol}-since-scan`}>
                  SINCE SCAN {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(2)}%
                </div>
              )}
            </>
          )}
          {(o.daily_change_pct ?? o.change_pct) !== undefined && (
            <div className={`mono text-[11px] ${(o.daily_change_pct ?? o.change_pct) >= 0 ? "pnl-up" : "pnl-down"}`}>
              {(o.daily_change_pct ?? o.change_pct) >= 0 ? "+" : ""}{(o.daily_change_pct ?? o.change_pct)?.toFixed(2)}%
            </div>
          )}
          <StateChips s={o.states}/>
        </div>
        <div className="grid grid-cols-3 gap-px bg-border/40 text-[10px]">
          <div className="p-1.5 bg-card/40"><div className="label-cap">Score</div><div className="mono text-sm">{o.score ?? "—"}</div></div>
          <div className="p-1.5 bg-card/40"><div className="label-cap">Conf</div><div className="mono text-sm">{o.confidence ?? "—"}</div></div>
          <div className="p-1.5 bg-card/40"><div className="label-cap">Risk</div><div className="mono text-sm">{o.risk_score ?? "—"}</div></div>
        </div>
        {(plan.entry || plan.stop || plan.target) && (
          <div className="grid grid-cols-4 gap-px bg-border/40 text-[10px]">
            <div className="p-1.5 bg-card/40"><div className="label-cap">Entry</div><div className="mono">${plan.entry?.toFixed(2)}</div></div>
            <div className="p-1.5 bg-card/40"><div className="label-cap">Stop</div><div className="mono pnl-down">${plan.stop?.toFixed(2)}</div></div>
            <div className="p-1.5 bg-card/40"><div className="label-cap">Target</div><div className="mono pnl-up">${plan.target?.toFixed?.(2) || (plan.targets?.[0]?.price?.toFixed(2))}</div></div>
            <div className="p-1.5 bg-card/40"><div className="label-cap">R/R</div><div className="mono">{plan.reward_risk?.toFixed(1) || "—"}:1</div></div>
          </div>
        )}
        {o.suggested_portfolio && (
          <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
            <span>Portfolio</span>
            <span className={`chip !text-[9px] ${o.fits_available_cash ? "text-signal-green border-current" : "text-signal-yellow border-current"}`}>
              {o.suggested_portfolio.replace("_"," ")} {o.fits_available_cash === false && "· cash short"}
            </span>
          </div>
        )}
        {o.thesis && <p className="text-[11px] text-muted-foreground leading-snug line-clamp-3">{o.thesis}</p>}
      </div>
      <div className="grid grid-cols-3 gap-px bg-border/40">
        <button className="btn-ghost h-8 !px-2 text-[10px]" onClick={() => onOpen(o)} data-testid={`opp-analyze-${o.symbol}`}>
          <Sparkles size={11}/> Open
        </button>
        <button className="btn-ghost h-8 !px-2 text-[10px]" onClick={() => onWatchlist(o)}>
          <Bookmark size={11}/> Watch
        </button>
        <button className="btn-ghost h-8 !px-2 text-[10px]" onClick={() => onDismiss(o)} data-testid={`opp-dismiss-${o.symbol}`}>
          <Ban size={11}/> Dismiss
        </button>
      </div>
    </div>
  );
}

function DailyBrief({ brief, onOpen, scanOverride }) {
  if (!brief) return null;
  // P1A/P1B/P1C (2026-08-18): single canonical effectiveTopPick +
  // suggestedActionText for every recommendation surface inside the
  // Scan Brief so no workspace-scoped field can leak.
  //
  // Semantic boundary (P1C):
  //   SCAN-SCOPED  → recommendation counts, top pick, suggested text,
  //                   review-first button, bucket totals
  //   WORKSPACE    → Cash Available, Market Regime, position alerts
  //
  const c = brief.opportunity_counts;
  const p = brief.position_alerts;
  const showScanBrief = !!scanOverride;
  const counts = showScanBrief ? {
    long_term: scanOverride.filter(o => (o.suggested_portfolio || o.category || "").toUpperCase() === "LONG_TERM").length,
    swing:     scanOverride.filter(o => (o.suggested_portfolio || o.category || "").toUpperCase() === "SWING").length,
    tactical:  scanOverride.filter(o => (o.suggested_portfolio || o.category || "").toUpperCase() === "TACTICAL").length,
  } : c.by_category;
  // Canonical effective top pick — the SINGLE decision consumed by
  // every recommendation-specific surface below.
  const effectiveTopPick = showScanBrief
    ? (() => {
        if (!scanOverride || scanOverride.length === 0) return null;
        const ranked = [...scanOverride].sort((a, b) => {
          const sa = a.score ?? -1, sb = b.score ?? -1;
          if (sb !== sa) return sb - sa;
          const ca = a.confidence ?? -1, cb = b.confidence ?? -1;
          if (cb !== ca) return cb - ca;
          return (a.symbol || "").localeCompare(b.symbol || "");
        });
        return ranked[0]?.symbol || null;
      })()
    : brief.top_pick;
  // P2: honest zero-recommendation empty state — never fall back to
  // workspace suggested_action inside BY-SCAN.
  const suggestedActionText = showScanBrief
    ? (effectiveTopPick
         ? `Review ${effectiveTopPick} first`
         : "No recommendations generated in this scan.")
    : brief.suggested_action;
  return (
    <div className="grid-cell !p-4 border-l-2 border-[#0A84FF]" data-testid="daily-brief">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#0A84FF]"/>
        <div className="mono uppercase tracking-widest text-[11px] text-[#0A84FF]"
             data-testid="daily-brief-title">
          {showScanBrief ? "Scan Brief" : "Daily Brief"} · {new Date(brief.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
        {effectiveTopPick && (
          <button className="btn-outline h-7 !px-2 text-[10px] ml-auto"
                  onClick={() => onOpen(effectiveTopPick)}
                  data-testid="brief-open-top-pick">
            <Star size={11}/> Review {effectiveTopPick} first
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border/60 text-xs">
        <div className="p-2 bg-card/40"><div className="label-cap">Long-Term</div><div className="mono text-lg" data-testid="brief-long-term">{counts.long_term}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Swing</div><div className="mono text-lg" data-testid="brief-swing">{counts.swing}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Tactical</div><div className="mono text-lg" data-testid="brief-tactical">{counts.tactical}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Near Target</div><div className="mono text-lg pnl-up">{showScanBrief ? "—" : p.near_target}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Near Stop</div><div className="mono text-lg pnl-down">{showScanBrief ? "—" : p.near_stop}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Cash Avail</div><div className="mono text-lg">${brief.total_available_cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
      </div>
      {brief.sector_concentration?.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px]">
          <span className="label-cap">Sector concentration:</span>
          {brief.sector_concentration.slice(0, 5).map((s) => (
            <span key={s.sector} className={`chip !text-[9px] ${s.pct > 30 ? "text-signal-yellow border-current" : "text-muted-foreground border-border/60"}`}>
              {s.sector} {s.pct}%
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 text-xs text-muted-foreground"
           data-testid="brief-suggested-line">
        Suggested: <span className="text-foreground"
                          data-testid="brief-suggested-action">{suggestedActionText}</span>
      </div>
    </div>
  );
}

const QUICK_CHIPS = [
  { id: "strong_buy",       label: "Strong Buy",         filter: { verdict: "STRONG_BUY" } },
  { id: "buy",              label: "Buy",                filter: { verdict: "BUY" } },
  { id: "buy_on_pullback",  label: "Buy on Pullback",    filter: { verdict: "BUY_ON_PULLBACK" } },
  { id: "long_term",        label: "Long-Term",          filter: { category: "long_term" } },
  { id: "swing",            label: "Swing",              filter: { category: "swing" } },
  { id: "tactical",         label: "Tactical",           filter: { category: "tactical" } },
  { id: "under_25",         label: "Under $25",          filter: { max_price: 25 } },
  { id: "under_50",         label: "Under $50",          filter: { max_price: 50 } },
  { id: "high_conviction",  label: "High Conviction",    filter: { min_confidence: 80 } },
  { id: "low_risk",         label: "Low Risk",           filter: { max_risk: 40 } },
  { id: "not_owned",        label: "Not Owned",          filter: { exclude_owned: true } },
  { id: "fits_cash",        label: "Fits Available Cash", filter: { only_fits_cash: true } },
];

// Client-side lifecycle filter chips — filter loaded opps by opp_lifecycle_state.
// URL-persisted via `lifecycle` param (comma-separated group ids).
const LIFECYCLE_GROUPS = [
  { id: "needs_review",   label: "Needs Review",     states: ["NEW", "VIEWED"] },
  { id: "watching",       label: "Watching",         states: ["SAVED", "WATCHING"] },
  { id: "in_progress",    label: "In Progress",      states: ["FULL_ANALYSIS_REQUESTED", "FULL_ANALYSIS_RUNNING", "FULL_ANALYSIS_COMPLETED"] },
  { id: "ready_to_trade", label: "Ready to Trade",   states: ["PROPOSAL_CREATED", "APPROVED"] },
  { id: "open_positions", label: "Open Positions",   states: ["PAPER_ORDER_SUBMITTED", "PAPER_POSITION_OPEN"] },
  { id: "completed",      label: "Completed",        states: ["CLOSED_WINNER", "CLOSED_LOSER", "CLOSED_BREAKEVEN"] },
  { id: "archived",       label: "Archived",         states: ["EXPIRED", "SUPERSEDED", "DISMISSED"] },
];

function Section({ title, items, icon: Ico, tone = "text-foreground", testId, onOpen, onDismiss, onWatchlist, liveQuotes, priceLabel }) {
  return (
    <section data-testid={testId} className="space-y-2">
      <div className="flex items-center gap-2">
        <Ico size={14} className={tone}/>
        <h2 className={`mono uppercase text-[11px] tracking-widest ${tone}`}>{title}</h2>
        <span className="mono text-[10px] text-muted-foreground">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground">No matches under current filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(o => (
            <OpportunityCard key={o.id} o={o} onOpen={onOpen} onDismiss={onDismiss} onWatchlist={onWatchlist}
                             liveQuote={liveQuotes?.[o.symbol?.toUpperCase()]}
                             priceLabel={priceLabel}/>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Opportunities() {
  const [brief, setBrief] = useState(null);
  const [buys, setBuys] = useState([]);
  const [sells, setSells] = useState([]);
  const [watch, setWatch] = useState([]);
  const [byPortfolio, setByPortfolio] = useState({ LONG_TERM: [], SWING: [], TACTICAL: [] });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  // Session-scope state (URL-persisted)
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = searchParams.get("scope") || "active";
  const scanSessionId = searchParams.get("scan_session_id");
  const lifecycleParam = searchParams.get("lifecycle") || "";
  const activeLifecycle = useMemo(
    () => lifecycleParam ? lifecycleParam.split(",").filter(Boolean) : [],
    [lifecycleParam]
  );
  // Chip selection lives in the URL as ?chips=under_25,swing so browser
  // back/forward + bookmark + share links reproduce the same view.
  const chipsParam = searchParams.get("chips") || "";
  const activeChips = useMemo(
    () => chipsParam ? chipsParam.split(",").filter(Boolean) : [],
    [chipsParam]
  );
  const setActiveChips = (updater) => {
    const next = typeof updater === "function"
      ? updater(activeChips) : updater;
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    if (next && next.length) params.chips = next.join(",");
    else delete params.chips;
    setSearchParams(params, { replace: false });
  };
  const [sessionOpps, setSessionOpps] = useState([]);
  const [total, setTotal] = useState(0);
  const [latestScan, setLatestScan] = useState(null);
  const [scanIntegrity, setScanIntegrity] = useState(null);
  // BY SCAN scope state
  const [scanList, setScanList] = useState([]);
  const evidenceFilter = searchParams.get("evidence") || "independent";
  const scanRunId = searchParams.get("scan_run_id") || null;
  // COMPARE SCANS mode (P2 Feb-2026): URL-persisted so back/forward works.
  const compareMode = searchParams.get("compare") === "1";
  const scanA = searchParams.get("scan_a") || null;
  const scanB = searchParams.get("scan_b") || null;
  // Ticker history drawer (P6): clicking "N SCANS" on a grouped card.
  const [historySymbol, setHistorySymbol] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const nav = useNavigate();
  const openAnalysis = useOpenAnalysis(nav);
  const confirm = useConfirm();

  function setScope(newScope, sid) {
    const params = { scope: newScope };
    if (sid) params.scan_session_id = sid;
    if (lifecycleParam) params.lifecycle = lifecycleParam;
    setSearchParams(params, { replace: false });
  }

  function setEvidenceFilter(val) {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    params.evidence = val;
    delete params.scan_run_id;  // reset selection when evidence changes
    setSearchParams(params, { replace: false });
  }

  function setScanRunId(id) {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    if (id) params.scan_run_id = id;
    else delete params.scan_run_id;
    params.scope = "by_scan";
    setSearchParams(params, { replace: false });
  }

  function enterCompareMode() {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    params.scope = "by_scan";
    params.compare = "1";
    setSearchParams(params, { replace: false });
  }
  function exitCompareMode() {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    delete params.compare;
    delete params.scan_a;
    delete params.scan_b;
    setSearchParams(params, { replace: false });
  }
  function setCompareA(id) {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    if (id) params.scan_a = id; else delete params.scan_a;
    setSearchParams(params, { replace: false });
  }
  function setCompareB(id) {
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    if (id) params.scan_b = id; else delete params.scan_b;
    setSearchParams(params, { replace: false });
  }

  async function loadTickerHistory(symbol) {
    setHistorySymbol(symbol);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const { data } = await api.get("/opportunities/history", {
        params: { symbol, evidence: "independent" },
      });
      setHistoryData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "History load failed");
    } finally { setHistoryLoading(false); }
  }

  function toggleLifecycle(id) {
    const next = activeLifecycle.includes(id)
      ? activeLifecycle.filter(x => x !== id)
      : [...activeLifecycle, id];
    const params = {};
    for (const [k, v] of searchParams.entries()) params[k] = v;
    if (next.length) params.lifecycle = next.join(",");
    else delete params.lifecycle;
    setSearchParams(params, { replace: false });
  }

  // Client-side lifecycle filter applied to loaded opps.
  const lifecycleStateSet = useMemo(() => {
    if (activeLifecycle.length === 0) return null;
    const set = new Set();
    for (const id of activeLifecycle) {
      const g = LIFECYCLE_GROUPS.find(x => x.id === id);
      if (g) for (const s of g.states) set.add(s);
    }
    return set;
  }, [activeLifecycle]);

  const filterByLifecycle = (rows) => {
    if (!lifecycleStateSet) return rows;
    return (rows || []).filter(o => lifecycleStateSet.has(o.opp_lifecycle_state || "NEW"));
  };

  // Client-side quick-chip filter — DEFENSIVE DISPLAY SAFEGUARD ONLY.
  // The backend `/api/opportunities` endpoint is the AUTHORITATIVE filter
  // engine (server-side pagination + total-count + CSV export all honor
  // these same filters). Kept here so a slow refetch never briefly shows
  // stale unfiltered results between filter changes.
  const applyChipFilters = (rows) => {
    const f = mergedFilters || {};
    return (rows || []).filter(o => {
      const price = o.price ?? o.current_price;
      const conf = o.confidence;
      const risk = o.risk_score;
      const verdict = o.verdict;
      const category = o.category;
      const port = o.suggested_portfolio;
      const owned = !!(o.states?.already_owned);
      const fits = o.fits_available_cash;
      if (f.verdict && verdict !== f.verdict) return false;
      if (f.category && category !== f.category && port !== f.category?.toUpperCase()) return false;
      if (f.suggested_portfolio && port !== f.suggested_portfolio) return false;
      if (f.max_price != null && price != null && price > f.max_price) return false;
      if (f.min_confidence != null && conf != null && conf < f.min_confidence) return false;
      if (f.max_risk != null && risk != null && risk > f.max_risk) return false;
      if (f.exclude_owned && owned) return false;
      if (f.only_fits_cash && fits === false) return false;
      return true;
    });
  };

  const mergedFilters = useMemo(() => {
    const f = { ...(activeView?.filters || {}) };
    for (const id of activeChips) {
      const chip = QUICK_CHIPS.find((c) => c.id === id);
      if (chip) Object.assign(f, chip.filter);
    }
    return f;
  }, [activeView, activeChips]);

  // ── Live-price overlay (P1/P3 Feb-2026): fetch current quotes for
  // every visible symbol in ONE batched call AFTER the persisted
  // cards render. NEVER blocks first paint — persisted card data
  // renders even if this hook is IDLE / FAILED.
  const visibleSymbols = useMemo(() => {
    const set = new Set();
    for (const o of buys) set.add(o.symbol);
    for (const o of sells) set.add(o.symbol);
    for (const o of watch) set.add(o.symbol);
    for (const o of byPortfolio.LONG_TERM) set.add(o.symbol);
    for (const o of byPortfolio.SWING) set.add(o.symbol);
    for (const o of byPortfolio.TACTICAL) set.add(o.symbol);
    for (const o of sessionOpps) set.add(o.symbol);
    return Array.from(set).filter(Boolean);
  }, [buys, sells, watch, byPortfolio, sessionOpps]);
  const { quotes: liveQuotes, priceLabel } = useLiveQuotes(visibleSymbols);

  // Progressive loader — cards must never be blocked by the brief or
  // by any single slow overlay. We fire card queries FIRST and settle
  // each response independently (allSettled) so a slow brief / regime
  // doesn't gate first-paint. (Feb-2026 perf audit.)
  const _val = (r) => (r?.status === "fulfilled" ? r.value?.data : null);

  const load = async () => {
    setLoading(true);
    try {
      const params = mergedFilters;
      // When a scan session scope is active, load ONLY that session's opps as a flat list.
      if (scope === "this_scan" || scope === "last_24h" || scope === "all" || scope === "by_scan") {
        // Send the full chip filter set to the backend — it's the authoritative
        // filter engine per user directive. Client-side applyChipFilters()
        // remains only as a defensive display safeguard.
        const q = {
          scope,
          ...mergedFilters,
          include_total: true,
          limit: 200,
        };
        if (scanSessionId) q.scan_session_id = scanSessionId;
        if (lifecycleParam) q.lifecycle = lifecycleParam;
        // P1: exact scan_run_id equality filter for BY SCAN.
        if (scope === "by_scan" && scanRunId) q.scan_run_id = scanRunId;
        const cardsPromise = scope === "by_scan" && !scanRunId
          ? Promise.resolve({ data: { items: [], total: 0 } })
          : api.get("/opportunities", { params: q });
        const [sessR, b, viewsR] = await Promise.allSettled([
          cardsPromise,
          api.get("/opportunities/brief"),
          api.get("/opportunity-views"),
        ]);
        const payload = _val(sessR);
        setSessionOpps(payload?.items ?? payload ?? []);
        setTotal(payload?.total ?? (payload?.items ?? payload ?? []).length);
        setLatestScan(payload?.latest_scan ?? null);
        setScanIntegrity(payload?.scan_integrity ?? null);
        setBrief(_val(b));                              // may be null; UI degrades gracefully
        setSavedViews(_val(viewsR) || []);
        setBuys([]); setSells([]); setWatch([]);
        setByPortfolio({ LONG_TERM: [], SWING: [], TACTICAL: [] });
      } else {
        const [buyR, sellR, watchR, ltR, swR, tacR, b, viewsR] = await Promise.allSettled([
          api.get("/opportunities/enhanced", { params: { ...params, verdict_group: "buy", limit: 12 } }),
          api.get("/opportunities/enhanced", { params: { ...params, verdict_group: "sell", limit: 12 } }),
          api.get("/opportunities/enhanced", { params: { ...params, verdict_group: "watch", limit: 8 } }),
          api.get("/opportunities/enhanced", { params: { verdict_group: "buy", suggested_portfolio: "LONG_TERM", limit: 6 } }),
          api.get("/opportunities/enhanced", { params: { verdict_group: "buy", suggested_portfolio: "SWING", limit: 6 } }),
          api.get("/opportunities/enhanced", { params: { verdict_group: "buy", suggested_portfolio: "TACTICAL", limit: 6 } }),
          api.get("/opportunities/brief"),
          api.get("/opportunity-views"),
        ]);
        setBuys(_val(buyR) || []);
        setSells(_val(sellR) || []);
        setWatch(_val(watchR) || []);
        setByPortfolio({
          LONG_TERM: _val(ltR) || [],
          SWING: _val(swR) || [],
          TACTICAL: _val(tacR) || [],
        });
        setBrief(_val(b));                              // slow brief never blocks cards
        setSavedViews(_val(viewsR) || []);
        setSessionOpps([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [mergedFilters, scope, scanSessionId, scanRunId, lifecycleParam]);

  // Load scan selector list when BY SCAN scope active or evidence changes.
  useEffect(() => {
    if (scope !== "by_scan") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/opportunities/scans", {
          params: { evidence: evidenceFilter, limit: 50 },
        });
        if (!cancelled) setScanList(data?.scans || []);
      } catch (e) {
        if (!cancelled) setScanList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, evidenceFilter]);

  const doScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/opportunities/scan", { top_n: 15 });
      toast.success(`Scan complete · ${data?.opportunities?.length || 0} new`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Scan failed");
    } finally { setScanning(false); }
  };
  const doDismiss = async (o) => {
    await api.post(`/opportunities/${o.id}/dismiss`);
    toast.success(`Dismissed ${o.symbol}`);
    await load();
  };
  const doWatchlist = async (o) => {
    toast.info(`Added ${o.symbol} to watchlist (manual step for now).`);
  };
  const onOpen = (o) => {
    const sym = typeof o === "string" ? o : o?.symbol;
    if (!sym) return;
    // Default to Chart tab — user can see market data even before AI runs.
    window.dispatchEvent(new CustomEvent("tm:open-drawer", { detail: { symbol: sym, tab: "chart" } }));
  };

  const saveCurrentView = async () => {
    if (!saveName.trim()) return;
    await api.post("/opportunity-views", { name: saveName.trim(), filters: mergedFilters, sort_by: "score" });
    toast.success(`View '${saveName}' saved`);
    setSaveName(""); setSaveDialogOpen(false);
    const { data } = await api.get("/opportunity-views"); setSavedViews(data);
  };
  const applyView = (v) => { setActiveView(v); setActiveChips([]); };
  const clearAll = () => { setActiveView(null); setActiveChips([]); };
  const exportCsv = () => {
    // Build URL with same filters going to the CSV endpoint.
    const p = new URLSearchParams();
    p.set("scope", scope);
    if (scanSessionId) p.set("scan_session_id", scanSessionId);
    if (lifecycleParam) p.set("lifecycle", lifecycleParam);
    Object.entries(mergedFilters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && v !== false) p.set(k, v);
    });
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${base}/api/opportunities.csv?${p.toString()}`, "_blank");
  };
  const deleteView = async (v) => {
    const ok = await confirm.confirm({
      variant: "destructive", title: `Delete view '${v.name}'?`,
      description: "This cannot be undone.", confirmLabel: "Delete", testId: `delete-view-${v.id}`,
    });
    if (!ok) return;
    await api.delete(`/opportunity-views/${v.id}`);
    if (activeView?.id === v.id) setActiveView(null);
    const { data } = await api.get("/opportunity-views"); setSavedViews(data);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="opportunities-root">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-medium tracking-tight flex items-center gap-2"><Zap size={18}/> Opportunities</h1>
          <p className="text-xs text-muted-foreground">Your AI investment terminal — BUY · SELL · WATCH · fit-for-portfolio.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button className="btn-primary" onClick={doScan} disabled={scanning} data-testid="opp-scan-btn">
            {scanning ? <Loader2 className="animate-spin" size={13}/> : <Zap size={13}/>}
            Scan
          </button>
        </div>
      </div>

      {/* Session Scope chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="scope-chips">
        <span className="label-cap">Scope</span>
        {[
          { id: "active",    label: "Active" },
          { id: "last_24h",  label: "Last 24h" },
          { id: "this_scan", label: scanSessionId ? `This Scan · ${scanSessionId.slice(0,8)}` : "Latest Scan" },
          { id: "by_scan",   label: "By Scan" },
          { id: "all",       label: "All (incl. superseded)" },
        ].map((s) => (
          <button key={s.id}
            onClick={() => setScope(s.id, s.id === "this_scan" ? scanSessionId : null)}
            className={`chip cursor-pointer ${scope === s.id ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
            data-testid={`scope-${s.id}`}>
            {s.label}
          </button>
        ))}
        {scope !== "active" && (
          <button className="text-[10px] text-muted-foreground hover:text-foreground ml-2"
                  onClick={() => setScope("active")}
                  data-testid="scope-reset">Reset to Active</button>
        )}
      </div>

      {/* BY SCAN — evidence filter + scan selector (P1/P2/P3) */}
      {scope === "by_scan" && (
        <div className="grid-cell !p-3 space-y-2" data-testid="by-scan-controls">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-cap">Evidence</span>
            {["independent", "diagnostic", "all"].map((v) => (
              <button key={v}
                onClick={() => setEvidenceFilter(v)}
                className={`chip cursor-pointer ${evidenceFilter === v ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                data-testid={`evidence-${v}`}>
                {v.toUpperCase()}
              </button>
            ))}
            {!compareMode && (
              <button onClick={enterCompareMode}
                      className="chip cursor-pointer ml-auto border-[#0A84FF]/60 text-[#0A84FF] hover:bg-[#0A84FF]/10"
                      data-testid="enter-compare-mode">
                COMPARE SCANS
              </button>
            )}
          </div>
          {compareMode ? (
            <CompareScansView
              scanList={scanList}
              evidenceFilter={evidenceFilter}
              scanA={scanA}
              scanB={scanB}
              onChangeA={setCompareA}
              onChangeB={setCompareB}
              onExit={exitCompareMode}
              onOpen={onOpen}
            />
          ) : (
          <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-cap">Scan</span>
            {scanList.length === 0 ? (
              <span className="text-[11px] text-muted-foreground mono"
                    data-testid="by-scan-empty">
                {evidenceFilter === "independent"
                  ? "No independent research scan has completed yet."
                  : `No ${evidenceFilter} scans available.`}
              </span>
            ) : (
              <select
                value={scanRunId || ""}
                onChange={(e) => setScanRunId(e.target.value || null)}
                data-testid="scan-selector"
                className="bg-transparent border border-border/60 text-[11px] mono text-foreground px-2 py-1 rounded focus:border-[#0A84FF] focus:outline-none min-w-[380px]">
                <option value="">— Select scan —</option>
                {scanList.map((s) => {
                  const dt = s.started_at ? new Date(s.started_at) : null;
                  const label = dt ? dt.toLocaleString("en-US", {
                    timeZone: "America/New_York", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit", hour12: true,
                  }) + " ET" : "—";
                  return (
                    <option key={s.scan_run_id} value={s.scan_run_id}>
                      {s.market_session} · {label} · {s.recommendation_count ?? 0} recs · {s.evidence_classification}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          {scanRunId && (() => {
            const s = scanList.find((x) => x.scan_run_id === scanRunId);
            if (!s) return null;
            const dt = s.started_at ? new Date(s.started_at) : null;
            const label = dt ? dt.toLocaleString("en-US", {
              timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric",
              hour: "2-digit", minute: "2-digit", hour12: true,
            }) + " ET" : "—";
            const badgeTone = s.evidence_classification === "INDEPENDENT"
              ? "text-signal-green border-signal-green/40"
              : s.evidence_classification === "DUPLICATE_SESSION"
                ? "text-red-400 border-red-400/40"
                : "text-signal-yellow border-signal-yellow/40";
            return (
              <div className="border-t border-border/40 pt-2 mt-1"
                   data-testid="scan-provenance">
                <div className="label-cap">Scan Results</div>
                <div className="text-[12px] mono mt-1">
                  {s.market_session} · {label} · <span className="text-foreground">{s.recommendation_count ?? 0}</span> recommendations
                </div>
                <div className="text-[10px] mono text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                  <span>{s.scan_run_id}</span>
                  {s.scanner_policy_family && <span>· {s.scanner_policy_family}</span>}
                  {s.universe_version && <span>· {s.universe_version}</span>}
                  {s.pipeline_status && <span>· {s.pipeline_status}</span>}
                  <span className={`chip !text-[9px] ml-2 ${badgeTone}`}
                        data-testid="scan-evidence-badge">
                    {s.evidence_classification.replace(/_/g, " ")}
                  </span>
                  {s.evidence_exclusion_reason && (
                    <span className="chip !text-[9px] text-red-400 border-red-400/40">
                      {s.evidence_exclusion_reason.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                {/* P3/P5: reconciliation counts — always show, colored by reconciled */}
                <div className={`mt-1 text-[10px] mono ${s.reconciled ? "text-signal-green" : "text-signal-yellow"}`}
                     data-testid="scan-integrity-line">
                  Evidence integrity ·{" "}
                  <span>{s.recommendation_count ?? 0} generated</span> ·{" "}
                  <span>{s.opportunity_records ?? 0} opportunities</span> ·{" "}
                  <span>{s.snapshot_records ?? 0} snapshots</span> ·{" "}
                  <span className={s.reconciled ? "" : "text-red-400"}>
                    {s.reconciled ? "RECONCILED" : "MISMATCH"}
                  </span>
                </div>
                <div className={`text-[10px] mono ${sessionOpps.length === (s.recommendation_count ?? 0) ? "text-signal-green" : "text-signal-yellow"}`}
                     data-testid="scan-display-integrity-line">
                  Display integrity · <span data-testid="display-rendered-count">{sessionOpps.length}</span>{" "}
                  of <span data-testid="display-expected-count">{s.recommendation_count ?? 0}</span> rendered ·{" "}
                  <span className={sessionOpps.length === (s.recommendation_count ?? 0) ? "" : "text-red-400"}>
                    {sessionOpps.length === (s.recommendation_count ?? 0) ? "RECONCILED" : "MISMATCH"}
                  </span>
                </div>
                {scanIntegrity && !scanIntegrity.reconciled && (
                  <div className="mt-2 border border-signal-yellow/40 bg-signal-yellow/5 p-2 text-[10px] mono text-signal-yellow"
                       data-testid="scan-integrity-banner">
                    ⚠ {scanIntegrity.note}
                  </div>
                )}
              </div>
            );
          })()}
          </>
          )}
        </div>
      )}

      {!(scope === "by_scan" && compareMode) && (
      <DailyBrief brief={brief} onOpen={onOpen}
                  scanOverride={scope === "by_scan" && scanRunId ? sessionOpps : null}/>
      )}

      {!(scope === "by_scan" && compareMode) && (
      <RegimePanel regime={brief?.market_regime}/>
      )}

      {!(scope === "by_scan" && compareMode) && (
      <Reconsiderations onOpen={onOpen}/>
      )}

      {!(scope === "by_scan" && compareMode) && (
      <CoachingPanel compact/>
      )}

      {!(scope === "by_scan" && compareMode) && (
      <>
      {/* Saved Views + Quick Chips */}
      <div className="grid-cell !p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={12} className="text-muted-foreground"/>
          <span className="label-cap">Views</span>
          <button className={`chip cursor-pointer ${!activeView ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground"}`}
                  onClick={clearAll} data-testid="view-all">All</button>
          {savedViews.map((v) => (
            <div key={v.id} className="flex items-center gap-1">
              <button className={`chip cursor-pointer ${activeView?.id === v.id ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground"}`}
                      onClick={() => applyView(v)} data-testid={`view-${v.id}`}>
                {v.is_default && <Star size={9} className="text-signal-yellow"/>}
                {v.name}
              </button>
              <button className="text-[10px] text-muted-foreground hover:text-signal-red px-1" onClick={() => deleteView(v)} title="Delete view" data-testid={`delete-view-btn-${v.id}`}>×</button>
            </div>
          ))}
          <button className="btn-ghost h-6 !px-1 text-[10px] ml-auto" onClick={() => setSaveDialogOpen(true)} data-testid="save-view-btn">
            <Save size={10}/> Save current
          </button>
          <button className="btn-ghost h-6 !px-1 text-[10px]" onClick={exportCsv} data-testid="export-csv-btn">
            Export CSV
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/30">
          <span className="label-cap">Chips</span>
          {QUICK_CHIPS.map((c) => {
            const active = activeChips.includes(c.id);
            return (
              <button key={c.id}
                onClick={() => setActiveChips(a => a.includes(c.id) ? a.filter(x => x !== c.id) : [...a, c.id])}
                className={`chip cursor-pointer transition-colors ${active ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                data-testid={`chip-${c.id}`}>
                {c.label}
              </button>
            );
          })}
          {activeChips.length > 0 && (
            <button className="text-[10px] text-muted-foreground hover:text-signal-red ml-2" onClick={() => setActiveChips([])}>Clear chips</button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/30" data-testid="lifecycle-chip-row">
          <span className="label-cap">Lifecycle</span>
          {LIFECYCLE_GROUPS.map((g) => {
            const active = activeLifecycle.includes(g.id);
            return (
              <button key={g.id}
                onClick={() => toggleLifecycle(g.id)}
                aria-pressed={active}
                className={`chip cursor-pointer transition-colors ${active ? "border-signal-blue text-signal-blue" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                data-testid={`lifecycle-chip-${g.id}`}>
                {g.label}
              </button>
            );
          })}
          {activeLifecycle.length > 0 && (
            <button className="text-[10px] text-muted-foreground hover:text-signal-red ml-2"
              onClick={() => {
                const params = {};
                for (const [k, v] of searchParams.entries()) if (k !== "lifecycle") params[k] = v;
                setSearchParams(params, { replace: false });
              }}
              data-testid="lifecycle-clear-btn"
            >Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin" size={14}/> Loading…</div>
      ) : scope !== "active" ? (
        (() => {
          // P4/P7: BY-SCAN preserves raw scan evidence — never apply
          // client-side lifecycle or chip filters that would drop
          // observations. Combined scopes (last_24h/all) can still
          // filter as before.
          const rawScanOpps = scope === "by_scan"
            ? (sessionOpps || [])
            : applyChipFilters(filterByLifecycle(sessionOpps));
          const sessionVisible = rawScanOpps;
          // P5/P7: consolidate by symbol ONLY for combined multi-scan
          // views (last_24h + all). BY SCAN and LATEST SCAN are
          // single-scan views — never collapse there.
          const shouldGroup = scope === "last_24h" || scope === "all";
          const groups = shouldGroup ? (() => {
            const map = new Map();
            for (const o of sessionVisible) {
              const sym = (o.symbol || "").toUpperCase();
              if (!map.has(sym)) map.set(sym, []);
              map.get(sym).push(o);
            }
            // Sort each group newest first and expose scan counts.
            const arr = [];
            for (const [sym, items] of map) {
              items.sort((a, b) =>
                new Date(b.created_at || 0) - new Date(a.created_at || 0));
              const scanIds = new Set();
              for (const it of items) if (it.scan_run_id) scanIds.add(it.scan_run_id);
              arr.push({ symbol: sym, latest: items[0], all: items,
                          scans: scanIds.size, count: items.length });
            }
            // Sort groups by latest observation.
            arr.sort((a, b) => new Date(b.latest?.created_at || 0) - new Date(a.latest?.created_at || 0));
            return arr;
          })() : null;

          const label = scope === "this_scan" ? "Latest Scan Results"
                       : scope === "by_scan" ? "This Scan Results"
                       : scope === "last_24h" ? "Last 24 Hours"
                       : "All Opportunities";
          const countLabel = shouldGroup
            ? `${groups.length} unique · ${sessionVisible.length} observations`
            : `${sessionVisible.length}${total > sessionVisible.length ? ` of ${total}` : ""}`;

          return (
        <div className="space-y-2" data-testid="session-opps-view">
          <div className="flex items-center gap-2">
            <span className="mono uppercase text-[10px] tracking-widest text-[#0A84FF]">
              {label}
            </span>
            <span className="mono text-[10px] text-muted-foreground">({countLabel})</span>
          </div>
          {sessionVisible.length === 0 ? (
            <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground"
                 data-testid="opportunities-empty-state">
              {scope === "this_scan"
                ? (
                  <div className="space-y-2">
                    <div className="text-signal-yellow" data-testid="latest-scan-empty-message">
                      {latestScan?.empty_state
                        || "No unattended research scan has completed yet."}
                    </div>
                    {latestScan?.scan_run_id && (
                      <div className="mono text-[10px] text-muted-foreground">
                        Latest independent run: {latestScan.market_session || "—"} · {latestScan.scan_run_id.slice(0, 12)}
                      </div>
                    )}
                  </div>
                )
                : scope === "by_scan" && !scanRunId
                  ? "Select a scan from the dropdown above."
                  : "No opportunities in this scope."}
            </div>
          ) : shouldGroup ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                 data-testid="opps-grouped-grid">
              {groups.map((g) => (
                <div key={g.symbol} className="relative"
                     data-testid={`opp-group-${g.symbol}`}>
                  <OpportunityCard o={g.latest} onOpen={onOpen}
                    onDismiss={doDismiss} onWatchlist={doWatchlist}/>
                  {g.scans > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); loadTickerHistory(g.symbol); }}
                      className="absolute top-2 right-2 chip !text-[9px] text-[#0A84FF] border-[#0A84FF]/60 hover:bg-[#0A84FF]/10 cursor-pointer"
                      data-testid={`opp-scans-chip-${g.symbol}`}
                      title="Click to see recommendation history">
                      {g.scans} SCANS
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sessionVisible.map((o, idx) => (
                <OpportunityCard key={o.id || `${o.symbol}-${idx}`}
                                  o={o} onOpen={onOpen}
                                  onDismiss={doDismiss} onWatchlist={doWatchlist}
                                  liveQuote={liveQuotes?.[o.symbol?.toUpperCase()]}
                                  priceLabel={priceLabel}/>
              ))}
            </div>
          )}
        </div>
          );
        })()
      ) : (
        <>
          <Section title="AI Recommended Buys" items={filterByLifecycle(buys)} icon={TrendingUp} tone="text-signal-green" testId="section-buys" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>
          <Section title="AI Recommended Sells" items={filterByLifecycle(sells)} icon={TrendingDown} tone="text-signal-red" testId="section-sells" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>
          <Section title="Watch Closely" items={filterByLifecycle(watch)} icon={Eye} tone="text-muted-foreground" testId="section-watch" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>

          <div className="pt-2">
            <h2 className="mono uppercase text-[11px] tracking-widest text-muted-foreground mb-2">Portfolio Opportunities</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Section title="Long-Term" items={filterByLifecycle(byPortfolio.LONG_TERM)} icon={Star} tone="text-[#0A84FF]" testId="section-portfolio-LONG_TERM" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>
              <Section title="Swing" items={filterByLifecycle(byPortfolio.SWING)} icon={Star} tone="text-signal-green" testId="section-portfolio-SWING" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>
              <Section title="Tactical" items={filterByLifecycle(byPortfolio.TACTICAL)} icon={Star} tone="text-signal-yellow" testId="section-portfolio-TACTICAL" onOpen={onOpen} onDismiss={doDismiss} onWatchlist={doWatchlist} liveQuotes={liveQuotes} priceLabel={priceLabel}/>
            </div>
          </div>
        </>
      )}
      </>
      )}

      <Dialog open={saveDialogOpen} onOpenChange={(v) => !v && setSaveDialogOpen(false)}>
        <DialogContent className="!rounded-none border-border/70 max-w-md" data-testid="save-view-dialog">
          <DialogHeader><DialogTitle className="mono uppercase tracking-wider text-sm">Save Current View</DialogTitle></DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="label-cap">Name</div>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Morning High-Conviction Buys"
                   className="!rounded-none mono" data-testid="save-view-name"/>
            <div className="mono text-[10px] text-muted-foreground">
              Filters: {Object.entries(mergedFilters).map(([k, v]) => `${k}=${v}`).join(", ") || "(none)"}
            </div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={() => setSaveDialogOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={saveCurrentView} disabled={!saveName.trim()} data-testid="save-view-submit">
              <Save size={13}/> Save View
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historySymbol} onOpenChange={(v) => { if (!v) { setHistorySymbol(null); setHistoryData(null); } }}>
        <DialogContent className="!rounded-none border-border/70 max-w-2xl"
                       data-testid="ticker-history-dialog">
          <DialogHeader>
            <DialogTitle className="mono uppercase tracking-wider text-sm">
              {historySymbol} — Recommendation History
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="text-center text-[11px] text-muted-foreground py-8">
              <Loader2 className="inline animate-spin mr-2" size={13}/> Loading history…
            </div>
          ) : historyData?.history?.length ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto"
                 data-testid="ticker-history-list">
              {historyData.history.map((h, i) => {
                const dt = h.created_at ? new Date(h.created_at) : null;
                const label = dt ? dt.toLocaleString("en-US", {
                  timeZone: "America/New_York", month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit", hour12: true,
                }) + " ET" : "—";
                return (
                  <div key={h.id || i} className="border border-border/40 p-3 space-y-2"
                       data-testid={`history-entry-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="mono text-[11px] text-[#0A84FF]">
                        {h.market_session || "—"} · {label}
                      </span>
                      <span className="chip !text-[9px] border-border/60">{h.verdict || "—"}</span>
                      <span className="text-[10px] text-muted-foreground mono ml-auto">
                        {(h.scan_run_id || "").slice(0, 16)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px] mono">
                      <div><div className="label-cap">Score</div><div>{h.score ?? "—"}</div></div>
                      <div><div className="label-cap">Confidence</div><div>{h.confidence ?? "—"}</div></div>
                      <div><div className="label-cap">Risk</div><div>{h.risk_score ?? "—"}</div></div>
                      <div><div className="label-cap">Price</div><div>{h.price != null ? `$${Number(h.price).toFixed(2)}` : "—"}</div></div>
                      <div><div className="label-cap">Entry</div><div>{h.entry_price != null ? `$${Number(h.entry_price).toFixed(2)}` : "—"}</div></div>
                      <div><div className="label-cap">Stop</div><div>{h.stop_price != null ? `$${Number(h.stop_price).toFixed(2)}` : "—"}</div></div>
                      <div><div className="label-cap">Target</div><div>{h.target_price != null ? `$${Number(h.target_price).toFixed(2)}` : "—"}</div></div>
                      <div><div className="label-cap">R/R</div><div>{h.risk_reward ?? "—"}</div></div>
                      <div><div className="label-cap">Portfolio</div><div>{h.suggested_portfolio || "—"}</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-[11px] text-muted-foreground py-8">
              No independent recommendation history for {historySymbol}.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
